import app from '../helpers/app';
import {useEffect, useState} from 'react';
import {IEvent} from '../@interfaces/services/Events';
import {snake_case} from '@src/helpers/string-registres';

type ID = string | number | null;

type Params<Message = unknown> = {
    event: string | string[],
    generalChannel?: boolean,
    channel?: string,
    model?: string,
    modelId?: ID,
    private?: boolean,
    encrypted?: boolean,
    // whisper?: boolean,//TODO: revert this functional
    filterMessages?: (event: IEvent, payload: Message) => boolean,
    enabled?: boolean,
    enabledOnlyWhenAuthorized?: boolean,
};

type Result<Message = unknown> = {
    lastMessage: Message | null,
    lastEventName: string | null,
    lastEventFiredAt: number | null,
};

export function getSubscriber(params: Params<any>) {
    const ws = app('ws');

    let subscriber;
    if (params.generalChannel) {
        subscriber = ws.onPrivateMessage.bind(ws);
    } else if (params.encrypted) {
        subscriber = ws.onEncryptedPrivateMessage.bind(ws);
    } else if (params.private !== false) {
        subscriber = ws.onPrivateMessage.bind(ws);
    } else {
        subscriber = ws.onMessage.bind(ws);
    }

    return subscriber;
}

export function getChannelName(params: Params<any>) {
    if (params.channel) {
        return params.channel;
    }

    if (params.generalChannel) {
        return 'general';
    }

    const modelName = snake_case(params.model ?? 'unknown', '-');
    if (params.modelId != null) {
        return `model.${modelName}.${params.modelId}`;
    }

    return `model.${modelName}`;
}

export default function useWebSocket<Message = any>(params: Params<Message> & {
    generalChannel: true,
}): Result<Message>;
export default function useWebSocket<Message = any>(params: Params<Message> & {
    encrypted: true,
    private?: true
}): Result<Message>;
export default function useWebSocket<Message = any>(params: Params<Message> & {
    private: false,
    encrypted?: false
}): Result<Message>;
export default function useWebSocket<Message = any>(params: Params<Message> & {
    channel: string,
    model?: never,
    modelId?: never
}): Result<Message>;
export default function useWebSocket<Message = any>(params: Params<Message> & {
    channel?: never,
    model: string,
    modelId?: ID
}): Result<Message>;
export default function useWebSocket<Message = any>(params: Params<Message>): Result<Message> {
    const [lastMessage, setLastMessage] = useState<Result<Message>['lastMessage']>(null);
    const [lastEventName, setLastEventName] = useState<Result<Message>['lastEventName']>(null);
    const [lastEventFiredAt, setLastEventFiredAt] = useState<Result<Message>['lastEventFiredAt']>(null);

    const subscriber = getSubscriber(params);
    const channelName = getChannelName(params);
    const filter = params.filterMessages;

    useEffect(() => {
        if (params.enabledOnlyWhenAuthorized !== true) {
            return;
        }

        const subscription = app('auth').onUserDetailsChanged((event, {user}) => {
            if (!user) {
                app('ws').leave(channelName);
            }
        });

        return () => subscription.unsubscribe();
    }, [channelName, params.enabledOnlyWhenAuthorized]);

    useEffect(() => {
        if (
            params.enabled === false ||
            params.enabledOnlyWhenAuthorized === true && !app('auth').isAuthorized()
        ) {
            return;
        }

        const subscription = subscriber(
            channelName,
            params.event,
            (event: IEvent, payload: Message) => {
                if (filter != null && !filter(event, payload)) {
                    return;
                }

                setLastMessage(payload);
                setLastEventName((payload as any)?.eventName ?? event.name);
                setLastEventFiredAt(Date.now());
            }
        );

        return () => subscription.unsubscribe();
    }, [channelName, params.event, subscriber, filter, params.enabled, params.enabledOnlyWhenAuthorized]);

    return {
        lastMessage,
        lastEventName,
        lastEventFiredAt,
    };
}
